/* eslint-disable no-magic-numbers */
import imageDynamicsConstructor from './imageDynamics';

function gesturesHandlerConstructor(config) {
  let isZoomed = false;

  let initialFocusPoint;
  let currentFocusPoint;
  let previousFocusPoint;
  let initialPinch;
  let previousPinch;
  let pointerMoved = false;
  let pointerDown = false;

  const imageDynamics = imageDynamicsConstructor(config);

  const toggleZoom = (focusPoint = { x: 0, y: 0 }) => {
    if (isZoomed) {
      imageDynamics.resetZoom();
    } else {
      imageDynamics.discreteZoomIn(focusPoint);
    }
    isZoomed = !isZoomed;
  };

  const isEndOfSingleTouch = touchEvent => touchEvent.touches.length + touchEvent.changedTouches.length === 1;

  const isEndOfPinch = touchEvent => initialPinch && touchEvent.touches.length + touchEvent.changedTouches.length === 2;

  const handlePinchEnd = () => {
    isZoomed = imageDynamics.isZoomed();
    initialPinch = null;
  };

  const handleTouchEnd = touchEvent => {
    if (isEndOfSingleTouch(touchEvent)) {
      if (!pointerMoved) {
        toggleZoom(initialFocusPoint);
      }
      touchEvent.preventDefault();
    } else if (isEndOfPinch(touchEvent)) {
      handlePinchEnd();
    }
  };

  const getPinchProps = touches => ({
    touches: [...touches].map(touch => ({
      identifier: touch.identifier,
      clientX: touch.clientX,
      clientY: touch.clientY,
    })),
    center: {
      x: (touches[0].clientX + touches[1].clientX) / 2,
      y: (touches[0].clientY + touches[1].clientY) / 2,
    },
    distance: Math.sqrt(
      Math.abs(touches[1].clientX - touches[0].clientX) ** 2 + Math.abs(touches[1].clientY - touches[0].clientY) ** 2
    ),
  });

  const handlePinchStart = event => {
    initialPinch = {
      ...getPinchProps(event.touches),
      zoom: 1,
    };
    previousPinch = initialPinch;
  };

  const calculateMovement = (current, previous) => ({
    x: current.x - previous.x,
    y: current.y - previous.y,
  });

  const handlePinchMove = event => {
    const { touches } = event;
    const currentPinch = getPinchProps(touches);
    currentPinch.zoom = currentPinch.distance / previousPinch.distance;
    const focusPointTranslation = calculateMovement(currentPinch.center, previousPinch.center);
    imageDynamics.continuousZoom(currentPinch.zoom, currentPinch.center, focusPointTranslation);
    previousPinch = currentPinch;
  };

  const getFocusPoint = event => {
    const { clientX, clientY } = event.type.includes('touch') ? event.touches[0] : event;
    return {
      x: clientX,
      y: clientY,
    };
  };

  const updateProps = ({ containerRef }) => {
    imageDynamics.updateProps(containerRef);
  };

  const handleMouseDown = mouseEvent => {
    pointerMoved = false;
    pointerDown = true;
    if (isZoomed) {
      mouseEvent.stopPropagation();
    }
  };

  const handleMouseMove = mouseEvent => {
    pointerMoved = true;
    if (isZoomed) {
      imageDynamics.move(getFocusPoint(mouseEvent));
      if (pointerDown) {
        mouseEvent.stopPropagation();
      }
    }
  };

  const handleMouseUp = mouseEvent => {
    if (!pointerMoved) {
      toggleZoom(getFocusPoint(mouseEvent));
    }
    pointerMoved = false;
    pointerDown = false;
  };

  const handleMouseLeave = () => {
    if (isZoomed) {
      toggleZoom();
    }
  };

  const handleTouchStart = event => {
    pointerMoved = false;
    if (event.touches.length === 1) {
      initialFocusPoint = getFocusPoint(event);
      currentFocusPoint = getFocusPoint(event);
      previousFocusPoint = getFocusPoint(event);
    } else if (event.touches.length === 2) {
      handlePinchStart(event);
    }
  };

  const handleTouchMove = event => {
    pointerMoved = true;
    if (isZoomed) {
      event.stopPropagation();
      event.preventDefault();
      if (event.touches.length === 1) {
        currentFocusPoint = getFocusPoint(event);
        const movement = calculateMovement(currentFocusPoint, previousFocusPoint);
        imageDynamics.dragMove(movement);
        previousFocusPoint = currentFocusPoint;
      } else if (initialPinch && event.touches.length === 2) {
        handlePinchMove(event);
      }
    }
  };

  return {
    updateProps,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  };
}

export default gesturesHandlerConstructor;
